// @givmo/employer-module — WHERE the Security panels live in the host that renders them (AS-WEB8).
//
// ══ ONE SET OF PANELS, TWO HOSTS ═══════════════════════════════════════════════════════════════
//
// The panels render in the employer portal's Settings › Security tab (`apps/admin`) and on the company
// console's Admin Console › Security page (`apps/company`). Four facts differ between the two, and
// every panel reads them from here rather than spelling them at the call site:
//
//   • THE SSO TEST CALLBACK — the route a finished test sign-in lands on. Joined to the running origin
//     it is the redirect URI the operator registers in `EMPLOYER_SSO_ALLOWED_REDIRECT_URIS`, which the
//     backend matches EXACTLY (`sso-test-session.ts`), so a host's value is an operator contract in
//     every environment, never a refactor.
//   • WHERE THAT LANDING SENDS THE OWNER BACK — the portal's Security tab, the console's page.
//   • THE EMPLOYEES PAGE, where the roster's two-factor column lives (the saved Required line links to
//     it) — or `null` where this host serves none for the person (the console without its Donations
//     module), and the line then renders nothing: never a door onto a page that is not there.
//   • WHETHER THE PANELS SIT UNDER A TAB STRIP — the portal's Settings tabs want 16px of air above the
//     first group; the console's page header spaces its own content.
//
// The DEFAULT is the portal's, byte for byte, so `apps/admin`, which supplies nothing, renders exactly
// what it rendered before the move. The console hands its own through `SecurityHostProvider`.
//
// ⚠ THE CONSOLE'S ADDRESSES LIVE IN `apps/company`, NOT HERE: this package names no route of the
// organization family (`copy-law.test.ts`, the build law's ban), so the console composes its host
// from its own routes.

import { createContext, use, type ReactNode } from 'react';
import { PORTAL_PATHS } from '../../lib/module-paths';
import {
  SSO_SETTINGS_RETURN_PATH,
  SSO_SETTINGS_RETURN_TAB,
  SSO_TEST_CALLBACK_PATH,
} from './sso-test-session';

/** Where the test landing returns the Owner to: a route, and the search it needs. */
export interface SecurityReturn {
  to: string;
  search?: Readonly<Record<string, string>>;
}

/** The facts about the host the Security panels render in. */
export interface SecurityHost {
  /** The SSO test-callback route — an operator contract (`EMPLOYER_SSO_ALLOWED_REDIRECT_URIS`). */
  testCallbackPath: string;
  /** Where the test landing returns the Owner to. */
  testReturn: SecurityReturn;
  /** The Employees page, or `null` where this host serves none for the person. */
  employees: string | null;
  /** Whether the panels render under a tab strip (the portal's Settings tabs). */
  underTabStrip: boolean;
}

/** The employer portal's host — the default, byte-identical to the panels before the move. */
export const PORTAL_SECURITY_HOST: SecurityHost = {
  testCallbackPath: SSO_TEST_CALLBACK_PATH,
  testReturn: { to: SSO_SETTINGS_RETURN_PATH, search: { tab: SSO_SETTINGS_RETURN_TAB } },
  employees: PORTAL_PATHS.employees,
  underTabStrip: true,
};

const SecurityHostContext = createContext<SecurityHost>(PORTAL_SECURITY_HOST);

/** Hand the panels this host's facts. Absent ⇒ the portal's. */
export function SecurityHostProvider({
  host,
  children,
}: {
  host: SecurityHost;
  children: ReactNode;
}) {
  return <SecurityHostContext value={host}>{children}</SecurityHostContext>;
}

/** This host's facts — the portal's when no provider says otherwise. */
export function useSecurityHost(): SecurityHost {
  return use(SecurityHostContext);
}
